import { createSerializationAdapter } from "@tanstack/react-router";

const diagnostics = new WeakMap<Error, string>();
const references = new WeakMap<object, string>();

export function getErrorReference(error: unknown): string | undefined {
	if (typeof error !== "object" || error === null) return undefined;
	const value =
		references.get(error) ??
		("referenceId" in error ? error.referenceId : undefined);
	return typeof value === "string" && /^[A-Za-z0-9:_-]{1,100}$/.test(value)
		? value
		: undefined;
}

export function ensureErrorReference(error: unknown): string {
	// References are diagnostics, not credentials. Keep the fallback working
	// on HTTP client hosts where randomUUID is unavailable.
	const referenceId =
		getErrorReference(error) ??
		globalThis.crypto?.randomUUID?.() ??
		`error-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	if (typeof error === "object" && error !== null)
		references.set(error, referenceId);
	return referenceId;
}

export function recordRequestDiagnostic(error: Error, message: string) {
	diagnostics.set(error, message);
	error.message = message;
}

export function getRequestDiagnostic(error: unknown): string | undefined {
	return error instanceof Error ? diagnostics.get(error) : undefined;
}

export function shouldRedactRequestError(error: unknown): boolean {
	return (
		getRequestDiagnostic(error) === undefined &&
		getErrorReference(error) === undefined
	);
}

export const requestErrorSerializationAdapter = createSerializationAdapter({
	key: "umbrella-request-error",
	test: (value: unknown): value is Error =>
		value instanceof Error && (diagnostics.has(value) || references.has(value)),
	toSerializable: (error: Error) => ({
		message: diagnostics.get(error) ?? "The page could not be loaded.",
		referenceId: getErrorReference(error),
	}),
	fromSerializable: (
		value: string | { message: string; referenceId?: string },
	) => {
		const message = typeof value === "string" ? value : value.message;
		const error = new Error(message);
		delete error.stack;
		recordRequestDiagnostic(error, message);
		if (
			typeof value !== "string" &&
			value.referenceId &&
			/^[A-Za-z0-9:_-]{1,100}$/.test(value.referenceId)
		)
			references.set(error, value.referenceId);
		return error;
	},
});
